import { Check, Copy } from "@phosphor-icons/react";
import { type ReactNode } from "react";
import { useCopyToClipboard } from "../../components/controls/useCopyToClipboard";
import { trackButtonPressed } from "../../analytics/tracking";

export const CLI_COMMAND_LEAD =
  "Run these lines in your terminal to point your agent at the Gateway.";

function StepLabel({ n, children }: { n: string; children: ReactNode }) {
  return (
    <p
      className="text-[11px]"
      style={{ color: "var(--fg-1)", fontWeight: 600 }}
    >
      <span style={{ color: "var(--fg-3)", fontWeight: 400 }}>
        {n}&nbsp;&nbsp;
      </span>
      {children}
    </p>
  );
}

/* The pill-shaped copy control the design puts on the command box: a labelled button, not the
 * icon-only IconButton used elsewhere. Colors are tokens, not the design's light hex: this
 * block is also rendered in the Reconnect drawer, whose dark scope remaps them. */
function CopyPill({
  value,
  label,
  onCopied,
  onPressed,
}: {
  value: string;
  label: string;
  /** Only on a successful write — this is what the analytics press events hang off. */
  onCopied?: () => void;
  /** On the press itself, whether or not the clipboard accepted it. */
  onPressed?: () => void;
}) {
  const { copied, copy } = useCopyToClipboard(onCopied);

  return (
    <button
      type="button"
      /* Both pills read "Copy", so the label says which one — and carries the confirmation
         too, since an aria-label hides the "Copied" text from a screen reader. */
      aria-label={copied ? `${label} — copied` : label}
      className="flex h-[30px] shrink-0 items-center gap-1.5 rounded-[5px] border px-2.5 text-[13px] font-medium"
      style={{
        background: "var(--surface)",
        borderColor: "var(--border-2)",
        color: "var(--fg-1)",
      }}
      onClick={() => {
        onPressed?.();
        void copy(value);
      }}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}
export function CliCommandBlock({
  command,
  onCommandCopyPressed,
}: {
  command: string;
  /* Fires on the press rather than on a successful write. A clipboard the browser refuses
     is silent, and it must not strand a reader whose next stage is gated on it — the
     command stays selectable either way. */
  onCommandCopyPressed?: () => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <StepLabel n="01">Installation package</StepLabel>
        <div
          className="flex items-start justify-between gap-3 rounded-[5px] border p-4"
          style={{
            background: "var(--surface-2)",
            borderColor: "var(--border-2)",
          }}
        >
          <code
            className="whitespace-pre-wrap break-words text-[13px]"
            style={{ fontFamily: "var(--font-mono)", color: "var(--fg-1)" }}
          >
            {command}
          </code>
          <CopyPill
            value={command}
            label="Copy command"
            onCopied={() => trackButtonPressed("Copy command")}
            onPressed={onCommandCopyPressed}
          />
        </div>
      </div>
    </div>
  );
}
